import { Dispatch, SetStateAction, useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { hintStorage } from '@features/hints';

import { createProject } from './helpers/actions';
import { getProjectUsersNormalized } from './helpers/getProjectUsersNormalized';
import { IForm } from './types/form.interface';
import { IFormError } from './types/formError.interface';
import { AddProjectContextProps, ProjectUser, Roles } from './types/types';

const initialFormErrors = {
    projectNameError: '',
    categoryNameError: '',
    categoryIdError: '',
};

type ProjectUsersState = {
    ids: number[];
    entities: Record<number, ProjectUser>;
};

export const useAddProject = () => {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(true);
    const [step, setStep] = useState(1);
    const [isLoader, setIsLoader] = useState(true);
    const [form, setForm] = useState<IForm>({
        projectName: t('useAddProject.useState.form.new_object'),
        categoryId: '',
        categoryName: '',
    });
    const [formErrors, setFormErrors] = useState<IFormError>(initialFormErrors);

    const onFormChange = useCallback((name: string, value: string) => {
        setForm((prevState) => ({ ...prevState, [name]: value }));
        setFormErrors(initialFormErrors);
    }, []);
    const onLoaderChange = useCallback((value: boolean) => {
        setIsLoader(value);
    }, []);

    const [projectId, setProjectId] = useState(null);
    const [redirectUrl, setRedirectUrl] = useState(null);
    const [managers, setManagers] = useState<ProjectUsersState>({
        ids: [],
        entities: {},
    });

    const [foremans, setForemans] = useState<ProjectUsersState>({
        ids: [],
        entities: {},
    });

    const [purchases, setPurchases] = useState<ProjectUsersState>({
        ids: [],
        entities: {},
    });

    const onHideModal = useCallback(() => {
        setIsOpen(false);
        const reactDiv = document.querySelector('#react-ProjectTeam');
        if (step === 2) {
            window.location.href = redirectUrl;
        }
        setTimeout(() => {
            if (reactDiv) {
                reactDiv.remove();
            }
        }, 500);
    }, [redirectUrl, step]);

    const setProjectsUsers = useCallback((projectUsers: {
        manager: ProjectUser[];
        foreman: ProjectUser[];
        purchase_agent: ProjectUser[];
    }) => {
        if (projectUsers.manager) {
            const normalizedManagers = getProjectUsersNormalized(projectUsers.manager);
            setManagers(normalizedManagers);
        } else {
            setManagers({ ids: [], entities: {} });
        }
        if (projectUsers.foreman) {
            const normalizedForemans = getProjectUsersNormalized(projectUsers.foreman);
            setForemans(normalizedForemans);
        } else {
            setForemans({ ids: [], entities: {} });
        }
        if (projectUsers.purchase_agent) {
            const normalizedPurchases = getProjectUsersNormalized(projectUsers.purchase_agent);
            setPurchases(normalizedPurchases);
        } else {
            setPurchases({ ids: [], entities: {} });
        }
    }, []);
    const deleteProjectUser = (id: number, role: string) => {
        const updateState = (
            state: ProjectUsersState,
            setState: Dispatch<SetStateAction<ProjectUsersState>>
        ) => {
            const updatedUserIds = state.ids.filter((userId) => userId !== id);
            const updatedUserEntities = { ...state.entities };
            delete updatedUserEntities[id];

            setState({
                ids: updatedUserIds,
                entities: updatedUserEntities,
            });
        };

        if (role === Roles.MANAGER) {
            updateState(managers, setManagers);
        } else if (role === Roles.FOREMAN) {
            updateState(foremans, setForemans);
        } else if (role === Roles.PURCHASER) {
            updateState(purchases, setPurchases);
        }
    };
    const addProjectUser = (role: string, projectUser: ProjectUser) => {
        const updateState = (
            state: ProjectUsersState,
            setState: Dispatch<SetStateAction<ProjectUsersState>>
        ) => {
            const updatedUserIds = [projectUser.id, ...state.ids];
            const updatedUserEntities = { ...state.entities };
            updatedUserEntities[projectUser.id] = projectUser;

            setState({
                ids: updatedUserIds,
                entities: updatedUserEntities,
            });
        };

        if (role === Roles.MANAGER) {
            updateState(managers, setManagers);
        } else if (role === Roles.FOREMAN) {
            updateState(foremans, setForemans);
        } else if (role === Roles.PURCHASER) {
            updateState(purchases, setPurchases);
        }
    };

    const onStepChange = useCallback(() => {
        if (step === 1) {
            const formData = new FormData();
            formData.append('name', form.projectName);
            formData.append('category_id', form.categoryId);
            formData.append('category_name', form.categoryName);
            if (form?.projectName.trim().length === 0) {
                setFormErrors((prev) => ({
                    ...prev,
                    projectNameError: t('useAddProject.Input.Errors.EmptyField'),
                }));
                return;
            }
            setIsLoader(true);
            createProject(formData)
                .then((data) => {
                    setRedirectUrl(data.redirect_url);
                    setProjectId(data.project_id);
                    setProjectsUsers(data.project_users.project_users);
                    hintStorage.markCompleted('add-project');
                    setStep(2);
                })
                .finally(() => {
                    setIsLoader(false);
                });
        } else {
            setIsLoader(true);
            window.location.href = redirectUrl;
            onHideModal();
        }
    }, [form, onHideModal, redirectUrl, setProjectsUsers, step, t]);

    const contextValue: AddProjectContextProps = {
        projectId,
        managers,
        foremans,
        purchases,
        allProjectUsers: [
            ...Object.values(purchases.entities),
            ...Object.values(foremans.entities),
            ...Object.values(managers.entities),
        ],
        setProjectsUsers,
        deleteProjectUser,
        addProjectUser,
    };

    return {
        isOpen,
        onHideModal,
        onStepChange,
        step,
        isLoader,
        onLoaderChange,
        form,
        formErrors,
        onFormChange,
        contextValue,
    };
};
